/**
 * Created by Rodrigo Quesada on 17/02/17.
 */
import {Routes, Route, ActivatedRoute, ActivatedRouteSnapshot} from '@angular/router';
import {Observable} from 'rxjs';
import {NavigableNode, NavigableNodeDeps} from './navigable-node.class';
import {Screen} from './screen.class';
import {ScreensManager} from './screen-manager.class';
import {RouteConfig} from './sections.service';
import {AuthInfo} from '../../security/authorization/auth.guard';
import {Roles} from '../../../domain/authorization/roles';

export class StepConfiguration {
    screensManager?: any;
}

export class Step extends NavigableNode<Step, Screen> {

    TYPE = Step;
    CHILD_TYPE = Screen;

    stepNumber: number;

    refreshed$: Observable<boolean>;

    screensManager?: ScreensManager;

    readonly idToScreenMap: {[id: string]: Screen} = {};

    constructor(protected navigableNodeDeps: NavigableNodeDeps, protected route: Route, screens: Screen[]) {
        super(navigableNodeDeps, route, screens);
        if (route.data) {
            let stepConfig: StepConfiguration|undefined = route.data[RouteConfig.STEP];
            if (stepConfig) {

                this.screensManager = this._nvnm.injector.get(stepConfig.screensManager);
                this.pathMatches = path => this.screensManager!.pathMatches(path);
            }
        }

        this.refreshed$ = this.screensManager ? this.screensManager.refreshed$ : Observable.of(true);
    }

    authorized(): boolean {
        if (this.route.data) {
            const dataAuth = this.route.data['auth'];
            if (dataAuth) {
                const auth = dataAuth instanceof AuthInfo ? dataAuth : AuthInfo.DEFAULT;
                let user = this.navigableNodeDeps.authService.user;
                if (user) {
                    return user.hasAnyRole(auth.roles.map(k => Roles[k]));
                }
            }
        }
        return true;
    }

    isSkipped(): boolean {
        return !!this.screensManager && this.screensManager.skip();
    }

    init() {
        super.init();
        this._children.forEach(screen => this.idToScreenMap[screen.id] = screen);
        if (this.screensManager) this.screensManager.init(this);
    }

    protected _setCurrentChild(child: Screen|undefined, route: ActivatedRoute) {
        super._setCurrentChild(child, route);
        if (this.screensManager) this.screensManager.setCurrentScreen(child, route.snapshot);
    }

    static fromRoutes(navigableNodeDeps: NavigableNodeDeps, routes: Routes): Observable<Step> {
        return Step._fromRoutes(navigableNodeDeps, Step, routes);
    }

    static _fromRoute(navigableNodeDeps: NavigableNodeDeps, stepRoute: Route): Observable<Step> {
        let steps$: Observable<Step>;

        if (Step._isAssociatedRoute(stepRoute)) {
            steps$ = Screen.fromRoutes(navigableNodeDeps, stepRoute.children || [])
                .toArray()
                .map(screens => new Step(navigableNodeDeps, stepRoute, screens));
        }
        else {
            steps$ = Observable.of();
        }

        return steps$;
    }

    static _findAssociatedRoute(route: ActivatedRoute): ActivatedRoute|undefined {
        route = route.firstChild;
        if (!!route && Step._isAssociatedRoute(route.snapshot)) return route;
    }

    static _isAssociatedRoute(route: Route|ActivatedRouteSnapshot): boolean {
        return route instanceof ActivatedRouteSnapshot || !route.redirectTo;
    }

    protected _currentChildHasPreviousLeaf(): boolean {
        return this._delegateToScreensManager(m => m.hasPreviousScreen(), () => super._currentChildHasPreviousLeaf());
    }

    protected _currentChildHasNextLeaf(): boolean {
        return this._delegateToScreensManager(m => m.hasNextScreen(), () => super._currentChildHasNextLeaf());
    }

    protected _navigateToCurrentChildPreviousLeaf() {
        return this._delegateToScreensManager(
            m => this._delegateNavigation(() => m.previousScreenId(), () => m.navigateToPreviousScreen()),
            () => super._navigateToCurrentChildPreviousLeaf()
        );
    }

    protected _navigateToCurrentChildNextLeaf() {
        return this._delegateToScreensManager(
            m => this._delegateNavigation(() => m.nextScreenId(), () => m.navigateToNextScreen()),
            () => super._navigateToCurrentChildNextLeaf()
        );
    }

    protected _navigateToFirstLeaf() {
        return this._delegateToScreensManager(
            m => this._delegateNavigation(() => m.firstScreenId(), () => m.navigateToFirstScreen()),
            () => super._navigateToFirstLeaf()
        );
    }

    protected _navigateToLastLeaf() {
        return this._delegateToScreensManager(
            m => this._delegateNavigation(() => m.lastScreenId(), () => m.navigateToLastScreen()),
            () => super._navigateToLastLeaf()
        );
    }

    protected _skip(): boolean {
        return !this.authorized() || !!this.screensManager && this.screensManager.skip(); }

    // region Utils
    private _delegateToScreensManager<R>(delegate: (ScreensManager) => R, originalMethod: () => R): R {
        if (this.screensManager) return delegate(this.screensManager);
        else  return originalMethod();
    }

    private _delegateNavigation(idBasedNavigationCall: () => string|undefined, directNavigationCall: () => Observable<boolean>): Observable<boolean> {
        const screenId = idBasedNavigationCall();
        if (screenId !== undefined) {
            let screen = this.idToScreenMap[screenId];
            return screen.navigate();
        }
        else return directNavigationCall();
    }

    // endregion
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/components/sections/service/step.class.ts